import type { UFOInteractionContextType } from '@atlaskit/react-ufo/interaction-context';

import { getAnalyticsWebClient } from '@confluence/analytics-web-client';
import { getLogger } from '@confluence/logger';
import { fg } from '@confluence/feature-gating';

import { type Match } from './RouteManager';

type TransitionCallbackArgs = { match: Match };
type TransitionCallback = (args: TransitionCallbackArgs) => Promise<void>;
type OnPerformanceMetricStartCallback = (args: { match: Match }) => void;
type CallbackConfig = {
	callback: TransitionCallback;
	onlyCalledIfFunc?: OnlyCalledIfFunc;
	onRouteContinueCallback?: OnRouteContinueCallback;
	onPerformanceMetricStart?: OnPerformanceMetricStartCallback;
};
type OnRouteContinueCallback = (args: { match: Match }) => Promise<void>;
type OnlyCalledIfFuncArgs = { match: Match };
type OnlyCalledIfFunc = (args: OnlyCalledIfFuncArgs) => boolean;
type RejectPromiseFunc = (reason?: any) => void;
type ActiveCallbackPromise = {
	promise: Promise<void>;
	onRouteContinueCallback?: OnRouteContinueCallback;
	onPerformanceMetricStart?: OnPerformanceMetricStartCallback;
	reject: RejectPromiseFunc;
	isExternallyRejected: boolean;
};

export class ExternalRejectionError extends Error {
	constructor(message?: string) {
		super(message);
		this.name = 'ExternalRejectionError';
	}
}

// TODO MODES-6531 Implement timeouts into preloader callback promises. If promise takes too long it should be rejected so it doesn't indefinitely block route changes.
/**
 * GlobalTransitionPreloader is responsible for managing transition callbacks during route changes.
 * These callbacks can be useful if you need to execute some preloading logic before certain route transitions finish, e.g. live doc -> live doc transitions.
 * It ensures that callbacks are executed only when necessary and prevents outdated route changes
 * from being triggered due to quick successive navigations.
 *
 * This class maintains a list of registered callbacks, their unique identifiers, and conditions
 * under which they should be executed. It also handles the lifecycle of promises associated with
 * these callbacks to ensure proper synchronization and error handling.
 */

/*
	TransitionProcessingState:
		processing: We're currently processing transition callbacks (when processTransitionCallback is running)
		processing_completed: We've finished processing transition callbacks (right as onRouteContinue is called)
		not_processing: We're not processing transition callbacks (never started processing, or onTransitionComplete has been called)
*/
type TransitionProcessingState = 'processing' | 'processing_completed' | 'not_processing';

export class GlobalTransitionPreloader {
	private logger = getLogger('GlobalTransitionPreloader');
	private callbacks: Map<string, CallbackConfig> = new Map();
	private activelyRunningCallbackPromises: Record<string, ActiveCallbackPromise> = {};
	private latestPathname: string = '';
	private currentRouteDetails: { transitionId: number; callbackIds: string[]; pathname: string } = {
		transitionId: -1,
		callbackIds: [],
		pathname: '',
	};
	private transitionProcessingState: TransitionProcessingState = 'not_processing';
	private cachedCallbackIds: { pathname: string; callbackIds: string[] } | null = null;

	/**
	 * Registers a transition callback that will be awaited on a route change.
	 * The callback will only be invoked if the `onlyCalledIfFunc` returns true or if `onlyCalledIfFunc` is not provided. e.g. To check that a contentId is what you expect it should be. `onlyCalledIfFunc` should be very performant as it will run every route change.
	 *
	 * PERFORMANCE WARNING: `onlyCalledIfFunc` is executed every route change, and `callback` promise is awaited every time `onlyCalledIfFunc` returns `true` (or if it doesn't exist).
	 *                      If either of these functions are not very performant, it could cause undesirable performance degradation on every transition.
	 *                      Try to avoid any time-intensive invocations, especially in `onlyCalledIfFunc`. `onlyCalledIfFunc` ideally only runs a condition the `match` that it's passed, e.g. to check `match.params.contentId` or `match.pathname`.
	 *                      BEFORE REGISTERING A NEW TRANSITION CALLBACK, you must be confident that the negative performance impact is acceptable.
	 *
	 * @example registerTransitionCallback({
	 *     id: 'unique-id-related-to-my-code',
	 *     callback: ({match}) => {
	 *         // Do preloading logic, must return a promise
	 *         return new Promise((resolve) => {
	 *             const nextContentId = match.params?.contentId;
	 *             // do logic that is a hard pre-requisite for next route and cannot be lazy/concurrently loaded, ideally shouldn't be slow
	 *             resolve(); // route change gets unblocked after these callback promises are resolved
	 *         }
	 *     },
	 *     onlyCalledIfFunc: ({match}) => {
	 *         // Optional but recommended
	 *         const nextContentId = match.params?.contentId;
	 *         // Do very quick logic, ideally no logic that we need to wait for, this function is run every route change
	 *         if (isLiveDoc(contentId)) {
	 *             return true;
	 *         } else {
	 *             return false;
	 *         }
	 *     },
	 *     onPerformanceMetricStart: ({match}) => {
	 *         // Optional but recommended
	 *         const nextContentId = match.params?.contentId;
	 *         // Should be used to quickly start any page load performance metrics that you want to track for this route change
	 *         // If you don't use this, your existing page load metric will likely start after the preloading and after the route change.
	 *         // We want page load metrics to start before preloading, not after.
	 *     },
	 *     onRouteContinueCallback: async ({match}) => {
	 *		   // Optional, must return a promise (i.e. or be async)
	 *         const nextContentId = match.params?.contentId;
	 *         // Do very quick logic that will run only if we're about to transition routes, runs after callback promise.
	 *         // Can be used to do logic that you want to ensure doesn't occur on outdated route changes, e.g. connecting to nextContentId's collab provider
	 *     },
	 * });
	 *
	 * @param {Object} params - The parameters for the transition callback.
	 * @param {string} params.id - A unique identifier used to inform other components the callback was run, to clean up outdated promises, and to prevent duplicate callback registrations.
	 * @param {TransitionCallback} params.callback - The callback function to be executed during a route transition. Must return a promise.
	 * @param {OnlyCalledIfFunc} params.onlyCalledIfFunc - A function that determines whether the callback should be called.
	 * @param {OnRouteContinueCallback} params.onRouteContinueCallback - Callback function that must return a promise. This is called right after the transition callback promises finish but before we continue the route transition, the returned promise blocks route transition. This function won't be called if route has changed before callback promise resolves.
	 * @param {OnPerformanceMetricStartCallback} params.onPerformanceMetricStart - Callback function to start performance metrics. Called as we start running the transition callback promises.
	 * @returns {boolean} - Returns `true` if the transition callback was registered, `false` if it was not registered (due to already being registered or being mid-transition).
	 */
	registerTransitionCallback({
		id,
		callback,
		onlyCalledIfFunc,
		onRouteContinueCallback,
		onPerformanceMetricStart,
	}: {
		id: string;
		callback: TransitionCallback;
		onlyCalledIfFunc?: OnlyCalledIfFunc;
		onRouteContinueCallback?: OnRouteContinueCallback;
		onPerformanceMetricStart?: OnPerformanceMetricStartCallback;
	}): boolean {
		// We want to keep transition callbacks unique to guarantee we aren't duplicating work, so we tie callbacks to an ID. If there are multiple registrations of the same callback ID, we ignore registration attempts if we're already registered.
		// We don't allow registering transition callbacks while transitioning, to avoid race conditions leading to infinite route change loops.
		if (this.transitionProcessingState !== 'not_processing' || this.callbacks.has(id)) {
			return false;
		}

		this.callbacks.set(id, {
			callback,
			onlyCalledIfFunc,
			onRouteContinueCallback,
			onPerformanceMetricStart,
		});
		return true;
	}

	/**
	 * Processes transition callbacks during a route change, checking if we need to create promises from callbacks, and waiting for them to resolve/reject before continuing the route change.
	 * `processTransitionCallback` will set `this.isTransitionCallbackRunning` to true, preventing callback registration. RouteManager is responsible for clearing it via `onTransitionComplete`.
	 *
	 * @param {{ match: Match; onRouteContinue: () => void; transitionId: number; interactionContext?: UFOInteractionContextType | null }} args - The arguments containing the route `match` object, RouteManager `transitionId`, the `onRouteContinue` continuation function, and optional UFO interaction context.
	 *                                                                                     `onRouteContinue` is called once promises resolve and should finish executing the route change.
	 * @returns {boolean} - Returns `true` if processing transition callback and transition should not continue (until promise finishes and calls onRouteContinue).
	 *                      Returns `false` if not processing transition callback and routeChangeHandler should continue running.
	 */
	processTransitionCallback({
		match,
		transitionId,
		onRouteContinue,
		interactionContext,
		startPageLoadMetrics,
	}: {
		match: Match;
		transitionId: number;
		onRouteContinue: () => void;
		interactionContext?: UFOInteractionContextType | null;
		startPageLoadMetrics?: (
			name: string,
			ufoName: string | undefined,
			transitionId: number,
			contentId?: string,
		) => void;
	}): { isProcessingCallback: boolean; ufoStopHold?: () => void } {
		try {
			if (!this.callbacks.size) {
				// Update latest route details to keep this.getCurrentRouteCallbackIds() is accurate. If transitionId has changed, accurately reflect that there are no callbackIds for that route change.
				if (transitionId !== this.currentRouteDetails.transitionId) {
					this.currentRouteDetails = {
						transitionId,
						callbackIds: [],
						pathname: match?.pathname || '',
					};
				}
				return { isProcessingCallback: false };
			}

			this.transitionProcessingState = 'processing';

			const previousPathname = this.latestPathname;
			this.latestPathname = match?.pathname;
			if (this.latestPathname !== previousPathname) {
				// Cancel all currently running promises because we're going to a different route. Helps prevent multiple quick route changes from causing race condition that could send user to outdated route change.
				// If needed, `getCallbackPromises` below will create new promises for the latest route.
				this.cancelActivePromises();
			}

			const { callbackIds } = this.getCallbackIds({ match });
			const { callbackPromises, callbackPromiseIds, callbackPromiseObjects } =
				this.getCallbackPromises({ match, callbackIds });

			// Cancel outdated actively running promises that aren't for the latest callbackIds
			this.cleanupOutdatedCallbacks(callbackIds);

			if (!callbackPromises.length) {
				// If there are no callback promises, route change will immediately continue. Reset our callbacks state as we don't want registered callbacks to linger once the user leaves the route. Callbacks should be registered on page/etc load if needed.
				// EDGE CASE: If the user is on a live doc and quickly clicks a non-live-doc like a view page, then quickly clicks back into a live doc before the view page has loaded, the preload-live-doc callback may have been removed, slowing down load time into that live doc.
				//            We'll solve for this in the future by always checking if we should preload a live doc on any route change, by never unregistering that callback
				this.reset();

				return { isProcessingCallback: false };
			}

			// Start browser metrics page load
			startPageLoadMetrics?.(
				match?.name ?? '',
				match?.ufoName,
				transitionId,
				match?.params?.contentId,
			);

			// Ensure UFO page load metric continues tracking the page load from start of preloading. If we don't hold here, UFO metric finishes prematurely on route change.
			// We should stop holding right after the route change is completed.
			const ufoStopHold = interactionContext?.hold?.('GlobalTransitionPreloader');

			callbackPromiseObjects.forEach(({ onPerformanceMetricStart }) => {
				onPerformanceMetricStart?.({ match });
			});

			// Timeout for all promises to prevent indefinite waiting
			const TIMEOUT_MS = 10000; // 10 seconds
			let timeoutId: ReturnType<typeof setTimeout> | null = null;

			// Flag to prevent race conditions between timeout and promise completion
			// Only one of them should trigger the route transition
			let hasRouteTransitionCompleted = false;

			// Set up timeout if feature gate is enabled
			if (fg('confluence_live_doc_smooth_transition_timeout')) {
				timeoutId = setTimeout(() => {
					// Only execute if route transition hasn't been completed by promise resolution
					if (!hasRouteTransitionCompleted) {
						hasRouteTransitionCompleted = true;
						this.logger
							.error`Callback promises timed out after ${TIMEOUT_MS}ms, forcing route transition`;
						this.reset();
						onRouteContinue();
						void getAnalyticsWebClient().then((analyticsWebClient) => {
							analyticsWebClient.sendOperationalEvent({
								action: 'confluence_live_doc_smooth_transition_timeout',
								actionSubject: 'callback_promises_timed_out',
								attributes: {
									callbackPromiseIds: callbackPromiseIds.join(','),
								},
								source: 'GlobalTransitionPreloader',
							});
						});
					}
				}, TIMEOUT_MS);
			}

			// Handle all the promises and then send the user to the next route if appropriate.
			void Promise.allSettled(callbackPromises).then((promiseResults) => {
				// Only proceed if timeout hasn't already triggered the route transition
				if (hasRouteTransitionCompleted) {
					return;
				}

				// Mark that we're handling the route transition to prevent timeout from also executing
				hasRouteTransitionCompleted = true;

				// Clear timeout if callbacks completed first
				if (timeoutId) {
					clearTimeout(timeoutId);
				}

				const onRouteContinueCallbacks: Array<OnRouteContinueCallback> = [];
				let externallyRejectedCount = 0;
				promiseResults.forEach((result, index) => {
					if (result.status === 'rejected') {
						if (callbackPromiseObjects[index].isExternallyRejected) {
							externallyRejectedCount++;
						} else {
							this.logger
								.error`There was an error with a transition callback promise: ${result.reason}, active callbackPromiseIds: ${callbackPromiseIds.join(',')}`;
						}
					} else {
						const { onRouteContinueCallback } = callbackPromiseObjects[index];
						if (onRouteContinueCallback) {
							onRouteContinueCallbacks.push(onRouteContinueCallback);
						}
					}
				});

				if (externallyRejectedCount < callbackPromises.length) {
					const onRouteContinueCallbackPromises = onRouteContinueCallbacks.map((callback) =>
						callback({ match }),
					);
					// Only update state if most promises weren't externally rejected (indicating this is still the current transition)
					this.currentRouteDetails.callbackIds = callbackPromiseIds;
					this.currentRouteDetails.transitionId = transitionId;
					this.currentRouteDetails.pathname = match?.pathname || '';
					this.transitionProcessingState = 'processing_completed';

					// On successful next route, we clear the currently registered callbacks. Callbacks should be registered on page/etc load if needed.
					this.callbacks = new Map();
					this.activelyRunningCallbackPromises = {};

					void Promise.allSettled(onRouteContinueCallbackPromises).then(() => {
						// If all promises have been externally rejected, that means these promises are for an outdated route change, the user has since changed routes again, so we don't want to redirect the user to the route.
						// This avoids race condition scenarios where user goes from Doc A -> Doc B -> Doc C, but ends up on Doc B because its promises finished after Doc C's promises.
						// On successful next route, we clear the currently registered callbacks. Callbacks should be registered on page/etc load if needed.
						this.callbacks = new Map();
						this.activelyRunningCallbackPromises = {};

						onRouteContinue();
					});
				}
			});

			return {
				isProcessingCallback: true,
				ufoStopHold: ufoStopHold ? () => ufoStopHold?.() : undefined,
			};
		} catch (error) {
			this.logger
				.error`There was an error running processTransitionCallback: ${error}, registered callbackIds: ${Array.from(this.callbacks.keys()).join(',')}`;
		}

		return { isProcessingCallback: false };
	}

	/**
	 * Retrieve callback IDs that ran on the current route. Can be useful to determine if your page was already preloaded or not.
	 * Recommended to only run this if you are finished transitioning. If you may be mid-transition (unlikely unless using Prompt), use `getRouteCallbackIdsByMatch`.
	 *
	 * @returns {string[]} - List of registered transition callback IDs that ran on current route when transitioning into it
	 */
	getCurrentRouteCallbackIds(): string[] {
		return [...this.currentRouteDetails.callbackIds];
	}

	/**
	 * Retrieve callback IDs that will be run on the specified match. This function looks at this.transitionProcessingState to determine which callback IDs to return.
	 * Recommended to only run this if you may be mid-transition, otherwise `getCurrentRouteCallbackIds` is preferable.
	 * Can be useful to determine if the match will be preloaded or not during transition, e.g. to determine whether a Prompt callback shows prompt.
	 *
	 * @returns {string[]} - List of registered transition callback IDs that will run speciifed match when transitioning into it
	 */
	getRouteCallbackIdsByMatch(match: Match): string[] {
		if (!match?.pathname) return [];

		/**
		 *  - If GlobalTransitionPreloader.processTransitionCallback is processing the same match as the specified match:
		 *    - If we're mid-transition ('processing'), we use the memoized callback IDs that were set when `processingTransitionCallback` called `getCallbackIds`.
		 *      - If memoized callback IDs don't exist (e.g. calling this too quickly right after processingTransitionCallback is called), we fetch the callback IDs for the specified match.
		 *    - If we're finished processing ('processing_completed') but haven't finished transitioning yet, we use the current route callback IDs.
		 *
		 *  - If the specified match is different from the match being processed, or if we are not processing a match ('not_processing'), then we fetch the callback IDs for the specified match.
		 */
		if (
			this.transitionProcessingState === 'processing' &&
			this.cachedCallbackIds?.callbackIds &&
			this.cachedCallbackIds?.pathname === match.pathname
		) {
			return [...this.cachedCallbackIds.callbackIds];
		} else if (
			this.transitionProcessingState === 'processing_completed' &&
			this.currentRouteDetails.pathname === match.pathname
		) {
			return [...this.currentRouteDetails.callbackIds];
		} else {
			return this.getCallbackIds({ match }).callbackIds;
		}
	}

	/**
	 * Resets the preloader to its initial state by clearing all registered callbacks,
	 * canceling any actively running promises, and resetting internal state.
	 * This can be useful for cleanup or testing scenarios.
	 */
	reset(): void {
		this.cancelActivePromises();

		this.callbacks = new Map();
		this.activelyRunningCallbackPromises = {};
		this.latestPathname = '';
		this.currentRouteDetails.callbackIds = [];
		this.currentRouteDetails.pathname = '';
		this.cachedCallbackIds = null;

		// If we started processing, mark as processing completed. We only set to not_processing in onTransitionComplete, to disallow registering callbacks mid-transition.
		if (this.transitionProcessingState !== 'not_processing') {
			this.transitionProcessingState = 'processing_completed';
		}
	}

	/**
	 * Router is responsible for running this function when the route has technically changed (routeChangeHandler).
	 @param {number} transitionId - The transition ID of the route that has completed, coming from RouteManager
	 */
	onTransitionComplete(transitionId: number): void {
		// When GlobalTransitionPreloader processes transition callbacks, it sets its transition running flag to true.
		// When it's done processing transition callbacks and the route has changed, we should clear the transition running flag.
		// This is used to prevent race conditions caused by problematic globalTransitionPreloader callback registrations during route changes.
		// For instance: If a callback is registered mid-transition, then when `routeBlockHandler` is called a second time by `onRouteContinue`,
		//               processTransitionCallback may try to process it after it has already processed it, leading to an infinite loop.
		this.transitionProcessingState = 'not_processing';
		this.cachedCallbackIds = null;

		// Transition ID from RouteManager is stored in this.currentRouteDetails before we call `routeChangeHandler`.
		// If we processed a transition callback, it'll always be one behind RouteManager
		// If transition IDs are misaligned and currentRouteDetails.callbackIds is non-empty, we're in an invalid state likely due to routeBlockHandler/processTransitionCallback unexpectedly not being triggered
		// If we're in this invalid state, reset the current route details callback IDs to prevent components from thinking we're in a preloaded state when we're not.
		if (
			this.currentRouteDetails.transitionId !== transitionId &&
			this.currentRouteDetails.callbackIds.length > 0
		) {
			this.logger
				.error`GlobalTransitionPreloader is in an invalid state, transitionId mismatch, callbackIds are non-empty. Resetting callbackIds. Invalid callbackIds are: ${this.currentRouteDetails.callbackIds.join(',')}`;
			this.currentRouteDetails.callbackIds = [];
		}
	}

	/**
	 * Generates an array of callback ids that should be processed before the next transition, filtering out
	 * callbacks which are not supposed to run based on their `onlyCalledIfFunc` evaluation.
	 * Caches most recent array of callback IDs as `this.cachedCallbackIds`
	 * @param { match: Match } params The params for the method including the match object.
	 * @returns { callbackIds: Array<string> } An object containing an array of callback ids that should be processed
	 */
	getCallbackIds({ match }: { match: Match }): { callbackIds: Array<string> } {
		const callbackIds: Array<string> = [];

		if (!this.callbacks.size) {
			this.cachedCallbackIds = { pathname: match.pathname, callbackIds: [] };
			return { callbackIds };
		}

		this.callbacks.forEach(({ onlyCalledIfFunc }, callbackId) => {
			// Call our `onlyCalledIfFunc` if it's set, we're only waiting for this callback promise if it's `true` or if it's not set.
			const isCallAllowed = onlyCalledIfFunc ? onlyCalledIfFunc({ match }) : true;

			if (isCallAllowed) {
				callbackIds.push(callbackId);
			}
		});

		// Cache the result for the most recent invocation, may be fetched by `getRouteCallbackIdsByMatch` when we're mid-transition, avoids additional onlyCalledIfFunc calls.
		this.cachedCallbackIds = { pathname: match.pathname, callbackIds: [...callbackIds] };

		return { callbackIds };
	}

	/**
	 * Creates an array of promises from registered callbacks that should be executed
	 * during a route transition. This method checks conditions under which each callback
	 * should be called and manages the lifecycle of the promises.
	 * Will clean up outdated promises.
	 *
	 * @private
	 * @param { match: Match, callbackIds: Array<string> } params - The parameters for the method, containing the match object and callback IDs.
	 * @returns { callbackPromises: Array<Promise<void>>, callbackPromiseIds: Array<string> } An object containing an array of promises and their corresponding IDs.
	 */
	private getCallbackPromises({
		match,
		callbackIds,
	}: {
		match: Match;
		callbackIds: Array<string>;
	}): {
		callbackPromises: Array<Promise<void>>;
		callbackPromiseIds: Array<string>;
		callbackPromiseObjects: Array<ActiveCallbackPromise>;
	} {
		const callbackPromises: Array<Promise<void>> = [];
		const callbackPromiseIds: Array<string> = []; // Recommendation is to use this to help provide context to promise errors
		const callbackPromiseObjects: Array<ActiveCallbackPromise> = [];

		callbackIds.forEach((callbackId) => {
			const callbackConfig = this.callbacks.get(callbackId);
			if (!callbackConfig) return; // This should never happen
			const { callback, onRouteContinueCallback, onPerformanceMetricStart } = callbackConfig;
			const activeCallbackPromise = this.activelyRunningCallbackPromises[callbackId];
			// If we want to trigger a callback promise, return the already running one if we have it, otherwise create a new one.
			if (activeCallbackPromise) {
				callbackPromises.push(activeCallbackPromise.promise);
				callbackPromiseIds.push(callbackId);
				callbackPromiseObjects.push(activeCallbackPromise);
			} else {
				// Start the promise and store the promise and its reject function in state (this.processTransitionCallback may call `reject` if the user changes route)
				let callbackPromiseReject: RejectPromiseFunc;
				const callbackPromise = new Promise<void>((resolve, reject) => {
					callbackPromiseReject = reject;
					callback({ match }).then(resolve).catch(reject);
				});
				callbackPromises.push(callbackPromise);
				callbackPromiseIds.push(callbackId);
				const callbackPromiseObject = {
					promise: callbackPromise,
					onRouteContinueCallback,
					onPerformanceMetricStart,
					reject: (reason: string) => callbackPromiseReject(new ExternalRejectionError(reason)),
					isExternallyRejected: false,
				};
				callbackPromiseObjects.push(callbackPromiseObject);
				this.activelyRunningCallbackPromises[callbackId] = callbackPromiseObject;
			}
		});
		return { callbackPromises, callbackPromiseIds, callbackPromiseObjects };
	}

	/**
	 * Cancels all actively running callback promises by rejecting them and marking as externally rejected
	 */
	private cancelActivePromises(): void {
		Object.entries(this.activelyRunningCallbackPromises).forEach(([callbackId, { reject }]) => {
			reject();
			this.activelyRunningCallbackPromises[callbackId].isExternallyRejected = true;
			delete this.activelyRunningCallbackPromises[callbackId];
		});
	}

	/**
	 * Cleans up any callback promises that are no longer valid for the current route.
	 * @param validCallbackIds - The callback ids that are valid for the current route.
	 */
	private cleanupOutdatedCallbacks(validCallbackIds: Array<string>) {
		Object.keys(this.activelyRunningCallbackPromises).forEach((activelyRunningCallbackId) => {
			if (!validCallbackIds.includes(activelyRunningCallbackId)) {
				const activeCallbackPromise =
					this.activelyRunningCallbackPromises[activelyRunningCallbackId];
				if (activeCallbackPromise) {
					activeCallbackPromise.reject?.();
					activeCallbackPromise.isExternallyRejected = true;
					delete this.activelyRunningCallbackPromises[activelyRunningCallbackId];
				}
			}
		});
	}
}
